<script setup>
import {computed, watch, ref} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import {useUniqueValue} from '@/composables/useUniqueValue'
import vFormValidation from '@/vue/directives/formValidation'
import FormControlPlainText from "@/common/form-control/FormControlPlainText.vue"
import moment from "moment/moment"

const props = defineProps({
    formId: String,
    currentData: Object,
    field: Object,
    value: String
})

const componentsStore = useComponentsStore()
const componentStructureStore = useComponentStructureStore()
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const fieldInputRef = ref(null)

useUniqueValue(props, fieldInputRef)

const tableName = computed(() => formStore.value?.tableName)

const componentId = computed(() => componentsStore.componentIds[tableName.value])

const component = computed(() => componentsStore.items.find(o => o.id === componentId.value))

const nameField = computed(() => componentsStore.nameField(componentId.value))

const displayName = computed(() => component.value?.displayName)

const isDisplayNameSet = computed(() => !!displayName.value?.length)

const isTitleColumn = computed(() => {
    const bool = isDisplayNameSet.value && nameField.value === props.field.columnName

    if (bool) {
        props.field.required = false
    }

    return bool
})

const formSmall = computed(() => formStore.value?.formSmall)

watch(
    () => props.currentData,
    async (newValue) => {
        if (isTitleColumn.value && newValue) {
            const fields = componentStructureStore.get(componentId.value)

            let newStr = ''
            let hasSetValues = false

            const parts = displayName.value.split(',')
            for (const part of parts) {
                let str = part.trim()

                if (str[0] === "'") {
                    const len = str.length
                    str = str.slice(1, len - 1)
                    newStr += str

                } else {
                    const keys = Object.keys(newValue)
                    const columnName = str
                    if (keys.indexOf(columnName) > -1) {
                        const targetField = fields.find(o => o.columnName === columnName)
                        let val = newValue[columnName]

                        if (val) {
                            hasSetValues = true
                        }

                        let dateStr = ''
                        const type = targetField?.type
                        switch (type) {
                            case 'dateDate':
                                if (val) {
                                    dateStr = moment(val).format('DD.MM.YY')
                                }
                                newStr += dateStr
                                break

                            case 'datetime-local':
                                if (val) {
                                    dateStr = moment(val).format('DD.MM.YY HH:mm')
                                }
                                newStr += dateStr
                                break

                            case 'date':
                                if (val) {
                                    let format
                                    switch (targetField.dateFormat) {
                                        case 'dd MMM yyyy':
                                            format = 'DD MMM YYYY'
                                            break
                                        case 'dd MMM yyyy, HH:mm':
                                        default:
                                            format = 'DD MMM YYYY, HH:mm'
                                    }
                                    dateStr = moment(val * 1000).format(format)
                                }
                                newStr += dateStr
                                break

                            case 'relationshipOneToMany':
                                if (val) {
                                    const relComponent = componentsStore.items.find(o => o.id === targetField.categoryComponentId)
                                    const relTableName = relComponent.tableName
                                    const o = await itemDataStore.get({
                                        tableName: relTableName,
                                        id: val
                                    })
                                    const relNameField = componentsStore.nameField(relComponent.id)
                                    newStr += o[relNameField] || ''
                                }
                                break

                            case 'text':
                                if (typeof val === 'string') {
                                    val = val.trim()
                                }
                                newStr += val
                                break
                        }
                    }
                }
            }

            props.currentData[props.field.name] = hasSetValues ? newStr : ''
        }
    },
    {deep: true}
)

formStore.value?.setDefaultValue({
    name: props.field.name,
    value: ''
})
</script>

<template>
    <FormControlPlainText v-if="field.readonly || isTitleColumn" :value="currentData[field.name]" :form-id="formId"/>
    <input v-else :list="field.datalist ? 'datalist-' + field.id : null" :id="field.id" ref="fieldInputRef"
           v-model="currentData[field.name]" v-select="field.autofocus" v-form-validation :disabled="field.disabled"
           :minlength="field.minlength" :name="field.name" :pattern="field.pattern || undefined"
           :placeholder="field.placeholder || undefined" :required="field.required" :size="field.size" autocomplete="off"
           class="form-control" :class="{'is-invalid': field.error, 'form-control-sm': formSmall}" type="text">
</template>